import {
  CreateOfferDto,
  OfferDto,
  OfferAttachmentDto,
  UpdateOfferEvaluationDto,
  RejectOfferDto,
} from '@paladin-monorepo/common';
import API from '../api';

export const offersApi = {
  // Create a new offer for a inquiry
  async create(inquiryId: string, data: CreateOfferDto): Promise<OfferDto> {
    return API.post<OfferDto>(`/offers?inquiryId=${encodeURIComponent(inquiryId)}`, data).then((res) => res.data);
  },

  // Get a single offer by ID
  async get(id: string): Promise<OfferDto> {
    return API.get<OfferDto>(`/offers/${encodeURIComponent(id)}`).then((res) => res.data);
  },

  // Get all offers for a specific inquiry
  async listForInquiry(inquiryId: string): Promise<OfferDto[]> {
    return API.get<OfferDto[]>(`/offers?inquiryId=${encodeURIComponent(inquiryId)}`).then((res) => res.data);
  },

  // Update an existing offer
  async update(id: string, data: Partial<CreateOfferDto>): Promise<OfferDto> {
    return API.patch<OfferDto>(`/offers/${encodeURIComponent(id)}`, data).then((res) => res.data);
  },

  // Delete an offer
  async delete(id: string): Promise<void> {
    return API.delete<void>(`/offers/${encodeURIComponent(id)}`).then((res) => res.data);
  },

  // Submit an offer (change status from draft to submitted)
  async submit(id: string): Promise<OfferDto> {
    return API.patch<OfferDto>(`/submitted-offers/${encodeURIComponent(id)}`).then((res) => res.data);
  },

  /** Inquirer: enqueue AI review when none exists or last run failed (not when succeeded / in progress). */
  async requestAiReview(offerId: string): Promise<OfferDto> {
    return API.post<OfferDto>(`/offers/${encodeURIComponent(offerId)}/ai-review`, {}).then((res) => res.data);
  },

  // Update evaluation (decision and notes) for an offer
  async updateEvaluation(inquiryId: string, offerId: string, data: UpdateOfferEvaluationDto): Promise<OfferDto> {
    return API.patch<OfferDto>(
      `/offer-evaluations/${encodeURIComponent(offerId)}`,
      data
    ).then((res) => res.data);
  },

  // Admin: Accept an offer from review (change status from in-review to submitted)
  async acceptFromReview(id: string): Promise<OfferDto> {
    return API.post<OfferDto>(`/review-offers/${encodeURIComponent(id)}/accept`).then((res) => res.data);
  },

  // Admin: Reject an offer from review (change status from in-review to draft)
  async rejectFromReview(id: string, data: RejectOfferDto): Promise<OfferDto> {
    return API.post<OfferDto>(`/review-offers/${encodeURIComponent(id)}/reject`, data).then((res) => res.data);
  },

  // Upload an attachment to an offer
  async uploadAttachment(
    offerId: string,
    file: File,
    onProgress?: (progress: number) => void,
    description?: string
  ): Promise<OfferAttachmentDto> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    return API.post<OfferAttachmentDto>(`/offers/${encodeURIComponent(offerId)}/attachments`, formData, {
      onUploadProgress: (progress) => {
        if (onProgress) {
          onProgress(progress.progress ?? 0);
        }
      },
    }).then((res) => res.data);
  },

  // Delete an attachment from an offer
  async deleteAttachment(offerId: string, attachmentId: string): Promise<void> {
    return API.delete<void>(`/offers/${encodeURIComponent(offerId)}/attachments/${attachmentId}`).then(
      (res) => res.data
    );
  },

  // Reorder attachments on an offer
  async reorderAttachments(offerId: string, attachmentIds: string[]): Promise<OfferAttachmentDto[]> {
    return API.patch<OfferAttachmentDto[]>(`/offers/${encodeURIComponent(offerId)}/attachments/reorder`, {
      attachmentIds,
    }).then((res) => res.data);
  },

  // Download an attachment from an offer
  async downloadAttachment(offerId: string, attachmentId: string, filename: string): Promise<void> {
    const response = await API.get(`/offers/${encodeURIComponent(offerId)}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
